import React from "react";
import { Box, Button, Checkbox, HStack, Text, VStack } from "components-lib";
import { useAuth } from "contexts/AuthContext";

const IDLE_LIMIT_SECONDS = 15 * 60;
const WARNING_THRESHOLD_SECONDS = 14 * 60;

// const IDLE_LIMIT_SECONDS = 10;
// const WARNING_THRESHOLD_SECONDS = 5;

const getHasToken = () => Boolean(localStorage.getItem("token"));

const formatRemaining = (seconds) => {
    const safe = Math.max(0, seconds);
    const minutes = Math.floor(safe / 60);
    const secs = safe % 60;
    return `${String(minutes).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
};

export default function IdleLogoutOverlay() {
    const { logout } = useAuth();
        const [showWarning, setShowWarning] = React.useState(false);
        const [keepLogin, setKeepLogin] = React.useState(false);
    const [remainingSeconds, setRemainingSeconds] = React.useState(IDLE_LIMIT_SECONDS);
    const lastActivityRef = React.useRef(Date.now());
    const showWarningRef = React.useRef(false);

    React.useEffect(() => {
        showWarningRef.current = showWarning;
    }, [showWarning]);

    const resetTimer = React.useCallback((options = {}) => {
        const { hideWarning = true, clearKeepLogin = true } = options;
        lastActivityRef.current = Date.now();
        if (hideWarning) setShowWarning(false);
        if (clearKeepLogin) setKeepLogin(false);
        setRemainingSeconds(IDLE_LIMIT_SECONDS);
    }, []);

    React.useEffect(() => {
        const handleActivity = () => {
            if (!getHasToken()) return;
            if (showWarningRef.current) return;
            resetTimer({ hideWarning: false, clearKeepLogin: false });
        };

        const events = [
            "mousemove",
            "mousedown",
            "keydown",
            "scroll",
            "touchstart",
            "wheel",
        ];

        events.forEach((eventName) => window.addEventListener(eventName, handleActivity));
        return () => {
            events.forEach((eventName) =>
                window.removeEventListener(eventName, handleActivity)
            );
        };
    }, [resetTimer]);

    React.useEffect(() => {
        const interval = window.setInterval(() => {
            if (!getHasToken()) {
                setShowWarning(false);
                setKeepLogin(false);
                setRemainingSeconds(IDLE_LIMIT_SECONDS);
                return;
            }

            const idleSeconds = Math.floor((Date.now() - lastActivityRef.current) / 1000);
            const remaining = Math.max(IDLE_LIMIT_SECONDS - idleSeconds, 0);
            setRemainingSeconds(remaining);

            if (idleSeconds >= WARNING_THRESHOLD_SECONDS && idleSeconds < IDLE_LIMIT_SECONDS) {
                setShowWarning(true);
            }

            if (idleSeconds >= IDLE_LIMIT_SECONDS) {
                if (keepLogin) {
                    resetTimer({ hideWarning: true, clearKeepLogin: false });
                } else {
                    logout();
                }
            }
        }, 1000);

        return () => window.clearInterval(interval);
    }, [keepLogin, logout, resetTimer]);

    const handleKeepLogin = () => {
        setKeepLogin(true);
        resetTimer({ hideWarning: true, clearKeepLogin: false });
    };

    if (!showWarning) {
        return null;
    }

    return (
        <Box
            position="fixed"
            top="0"
            left="0"
            w="100vw"
            h="100vh"
            bg="blackAlpha.600"
            zIndex="9999"
            display="flex"
            alignItems="center"
            justifyContent="center"
        >
            <Box
                bg="white"
                borderRadius="12px"
                p="32px"
                minW={{ base: "90%", sm: "420px" }}
                boxShadow="2xl"
            >
                <VStack spacing={4} align="stretch">
                    <Text fontSize="lg" fontWeight="bold" color="red.500" textAlign="center">
                        Session idle. You will be logged out soon.
                    </Text>
                    <Text fontSize="2xl" fontWeight="bold" color="red.500" textAlign="center">
                        {formatRemaining(remainingSeconds)}
                    </Text>
                    {/* <HStack justify="center">
                        <Checkbox
                            colorScheme="red"
                            isChecked={keepLogin}
                            onChange={(e) => setKeepLogin(e.target.checked)}
                        >
                            <Text color="red.500">Keep login</Text>
                        </Checkbox>
                    </HStack> */}
                    <Button colorScheme="blue" onClick={handleKeepLogin}>
                        Keep login
                    </Button>
                    <Text fontSize="sm" color="red.500" textAlign="center">
                        You will be logged out at 15 minutes of inactivity.
                    </Text>
                </VStack>
            </Box>
        </Box>
    );
}
